import { format, parseISO } from 'date-fns';
import { colors } from './utils.colors';
import { getFormattedDate, getFormattedDuration } from './utils.date-time-formatters';

const severityMapping = {
  critical: {
    background: '#CC0000',
    label: 'Critical',
  },
  non_critical: {
    background: 'black',
    label: 'Not Critical',
  },
  informational: {
    background: 'black',
    label: 'Informational',
  },
};

// const trnasformedTooltip = Object.entries(tooltip).map(([key, value]: any) => ({
//   ...value,
//   alarm: key,
// }));

export function renderTopExternalAlarmsByComponentTooltip({
  chart_group_by,
  metric,
  colorMap,
}: {
  chart_group_by: string;
  metric: string;
  colorMap: Record<string, string>;
}) {
  return ({ chartData, params }: any) => {
    if (!chartData) return '';
    const { date, formattedDate } = getFormattedDate(params[0]?.axisValue);
    const { tooltip } = chartData.find((el: any) => el.date === date);

    const nonZeroParams = params;
    if (nonZeroParams.length === 0) return '';

    let content = `
        <div style="color:#01152B; font-family: Montserrat, sans-serif; border-bottom: 1px solid #c4c4c4; padding: 4px 0 2px; margin-bottom:2px">
          <strong style="font-weight: 500;">Top 20 ${chart_group_by} for ${formattedDate}</strong>
        </div>
      `;
    content += '<table style="font-family: Montserrat;color:#01152B;">';
    content += `
        <tr style="font-size: 10px; color: #55687D;">
          <td style="padding-right: 12px; text-transform: capitalize;">${chart_group_by}</td>
          <td style="padding-right:12px">Count</td>
          <td>Duration</td>
        </tr>
      `;
    nonZeroParams.forEach((param: any) => {
      const name = param?.data?.componentName || param?.seriesName;
      if (tooltip?.[name]) {
        content += `
        <tr>
          <td style="padding-right: 12px; display: flex; align-items: center;">
            <span style="background:${
              colorMap[name] || '#ccc'
            }; width:8px; height:8px; border-radius:50%; display:inline-block; margin-right:6px;"></span>
            ${name}
          </td>
          <td style="text-align:center">${tooltip?.[name]?.alarms_count}</td>
          <td style="text-align:center">${getFormattedDuration(tooltip?.[name]?.alarms_duration_seconds)}</td>
        </tr>
      `;
      }
    });
    content += '</table>';

    return content;
  };
}

export function renderTopExternalAlarmsByAlarmTooltip({
  chart_group_by,
  metric,
  colorMap,
}: {
  chart_group_by: string;
  metric: string;
  colorMap: Record<string, string>;
}) {
  return ({ chartData, params }: any) => {
    if (!chartData) return '';
    const { date, formattedDate } = getFormattedDate(params[0]?.axisValue);
    const { tooltip } = chartData.find((el: any) => el.date === date);

    const nonZeroParams = params;
    if (nonZeroParams.length === 0) return '';

    let content = `
        <div style="color:#01152B; font-family: Montserrat, sans-serif; border-bottom: 1px solid #c4c4c4; padding: 4px 0 2px; margin-bottom:2px">
          <strong style="font-weight: 500;">${formattedDate}</strong>
        </div>
      `;
    content += '<table style="font-family: Montserrat;color:#01152B;">';
    content += `
        <tr style="font-size: 10px; color: #55687D;">
          <td style="padding-right: 12px; text-transform: capitalize;">Alarm name & OEM ID</td>
          <td style="padding-right:12px; width:50px;">Severity</td>
          <td style="padding-right:12px; width:50px;">Priority</td>
          <td style="padding-right:12px; width:50px;">Count</td>
          <td>Duration</td>
        </tr>
      `;

    nonZeroParams.forEach((param: any) => {
      const name = param?.data?.componentName || param?.seriesName;
      const severityMap = severityMapping[tooltip?.[name]?.alarm_severity as 'critical'];

      if (tooltip?.[name]) {
        content += `
        <tr>
          <td style="padding-right: 12px; display: flex; flex-direction:column; align-items: flex-start;">
          <div>
            <span style="background:${
              colorMap[name] || '#ccc'
            }; width:8px; height:8px; border-radius:50%; display:inline-block; margin-right:6px;"></span>
            ${name?.split('@')?.[0]}
            </div>
            <div style="padding-left:16px; color:gray">OEM ID: ${tooltip?.[name]?.oem_id}</div>
          </td>
          <td style="padding-right:12px;"><span style="padding:4px 10px; font-weight:500;color:white; font-size:12px;  border-radius:50px; background:${
            severityMap?.background
          };">${severityMap?.label}</span></td>
          <td style="padding-right:12px;"><span style="padding:4px 10px; font-weight:500; font-size:12px;   border-radius:50px; background:black; color:white; ">${
            tooltip?.[name]?.priority
          }</span></td>
          <td style="padding-right:12px;text-align:center">${tooltip?.[name]?.alarms_count}</td>
          <td style="text-align:center">${getFormattedDuration(tooltip?.[name]?.alarms_duration_seconds)}</td>
        </tr>
      `;
      }
    });

    content += '</table>';
    return content;
  };
}

export function renderTopAlarmsTooltip({
  chart_group_by,
  metric,
  colorMap,
}: {
  chart_group_by: string;
  metric: string;
  colorMap: Record<string, string>;
}) {
  return ({ chartData, params }: any) => {
    if (!chartData) return '';
    const { date, formattedDate } = getFormattedDate(params[0]?.axisValue);
    const { tooltip } = chartData.find((el: any) => el.date === date);

    const nonZeroParams = params.filter((param: any) => !!param?.value);
    if (nonZeroParams.length === 0) return '';

    let content = `
        <div style="color:#01152B; font-family: Montserrat, sans-serif; border-bottom: 1px solid #c4c4c4; padding: 4px 0 2px; margin-bottom:2px">
          <strong style="font-weight: 500;">Top 7 ${chart_group_by} for ${formattedDate}</strong>
        </div>
      `;
    content += '<table style="font-family: Montserrat;color:#01152B;">';
    content += `
        <tr style="font-size: 10px; color: #55687D;">
          <td style="padding-right: 12px; text-transform: capitalize;">${chart_group_by}</td>
          <td>Alarms ${metric}</td>
        </tr>
      `;

    nonZeroParams.forEach((param: any) => {
      const name = param?.data?.componentName || param?.seriesName;
      content += `
          <tr>
            <td style="padding-right: 12px; display: flex; flex-direction:column; align-items: flex-start;">
          <div>
            <span style="background:${
              colorMap[name] || '#ccc'
            }; width:8px; height:8px; border-radius:50%; display:inline-block; margin-right:6px;"></span>
            ${name?.split('@')?.[0]}
            </div>`;
      if (name?.split('@')?.[1])
        content += `<div style="padding-left:16px; color:gray">OEM ID: ${name?.split('@')?.[1]}</div>`;
      content += `</td>
            <td>${metric === 'duration' ? getFormattedDuration(param?.value) : param?.value}</td>
          </tr>`;
    });

    content += '</table>';
    return content;
  };
}
